import {
  UseAsyncOperationProgressActions,
  UseAsyncOperationProgressState,
} from "@/components/progress/useAsyncOperationProgress";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import useMonthNames from "@/hooks/useMonthNames";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { getMonthsInRange } from "@/lib/date";
import { formatDate } from "@/lib/utils/formatters";
import { UpsertProjectLimit } from "@/types/projectLimit";
import { MutableRefObject, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ProjectLimitService } from "@/modules/projects/service/projectLimit";
import { createLimitsForStoresBasedOnValues } from "../utils/createLimitsForStoresBasedOnValues";
import useProjectLimitQueryInvalidator from "./useProjectLimitQueryInvalidator";

const STORE_BATCH_SIZE = 1000;

interface UseProjectLimitUpsertMutationProps {
  projectId: string;
  progress: UseAsyncOperationProgressState & UseAsyncOperationProgressActions;
  form: UseFormReturn<UpsertProjectLimit>;
  formValuesRef: MutableRefObject<{
    entityName: string;
    hours: string;
    rate: string;
    months: number;
    stores: number;
  } | null>;
  getSelectedStoreIds: () => string[];
}

export const useProjectLimitUpsertMutation = ({
  projectId,
  progress,
  form,
  formValuesRef,
  getSelectedStoreIds,
}: UseProjectLimitUpsertMutationProps) => {
  const { t } = useTranslation();
  const monthNames = useMonthNames();
  const projectLimitQueryInvalidator = useProjectLimitQueryInvalidator();

  const mutationFn = useCallback(
    async (values: Omit<UpsertProjectLimit, "entityName">) => {
      const { from, to, entityId, hours, rate, addition } = values;

      const months = getMonthsInRange(from, to);
      const storeIds = getSelectedStoreIds();

      formValuesRef.current = {
        entityName: form.getValues("entityName") || "",
        hours,
        rate,
        months: months.length,
        stores: storeIds.length,
      };

      // Split stores into chunks and build a flat task list (month × batch)
      const storeBatches: string[][] = [];
      for (let i = 0; i < storeIds.length; i += STORE_BATCH_SIZE) {
        storeBatches.push(storeIds.slice(i, i + STORE_BATCH_SIZE));
      }

      const showBatchInLabel = storeBatches.length > 1;

      const tasks = months.flatMap((month) => {
        const monthId = month.toISO() || month.toString();
        const monthLabel = `${monthNames[month.month - 1]} ${month.year}`;
        const period = formatDate(month.toJSDate(), DEFAULT_DATE_FORMAT);

        return storeBatches.map((storeIdsBatch, batchIndex) => ({
          id: `${monthId}-batch-${batchIndex}`,
          label: showBatchInLabel
            ? `${monthLabel} (${batchIndex + 1}/${storeBatches.length})`
            : monthLabel,
          period,
          storeIdsBatch,
        }));
      });

      progress.initialize(
        tasks.map(({ id, label }) => ({ id, label, success: false })),
      );
      progress.startProcessing();

      try {
        for (const task of tasks) {
          if (progress.isCancelling()) break;

          progress.setCurrentItem(task.label);

          try {
            await ProjectLimitService.patch(
              projectId,
              createLimitsForStoresBasedOnValues(
                task.period,
                task.storeIdsBatch,
                entityId,
                { hours, rate, addition },
              ),
            );
            progress.markItemProcessed(task.id, true);
          } catch (error) {
            progress.markItemProcessed(task.id, false);
            throw error;
          }
        }
      } finally {
        progress.complete();
      }
    },
    [monthNames, projectId, progress, formValuesRef, form, getSelectedStoreIds],
  );

  return useMutationWithErrorHandling({
    mutationFn,
    errorTitle: t("projectLimits.errors.updatePeriods"),
    onSuccess: () => {
      projectLimitQueryInvalidator.invalidateAll();
    },
  });
};
